<template>
    <div>
        <CompassAlert :type="CompassEnums.AlertType.INFORMATIVE" :description="hint" />
        <div>
            <CompassTable
                :size="CompassEnums.TableSize.MEDIUM"
                :hasExpandableRows="props.isStockEnabled"
            >
                <CompassTableHead>
                    <CompassTableHeadCell>
                        {{ $t.__('ocr.OcrVerificationProductsTable.supplier_product') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell
                        v-if="props.isLineItemsEnabled"
                        class="ocr-verification-products-table__header--align-right"
                    >
                        {{ $t.__('common.labels.quantity_shorten') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell
                        class="ocr-verification-products-table__header--align-right"
                    >
                        {{ $t.__('common.labels.cost_excluding_tax') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell>
                        {{ $t.__('ocr.OcrVerificationForm.tax_rate') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell v-if="props.isLineItemsEnabled">
                        {{ $t.__('ocr.OcrVerificationProductsTable.unit_optional') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell
                        class="ocr-verification-products-table__header--align-right"
                    >
                        {{ $t.__('ocr.OcrVerificationProductsTable.tax_amount') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell
                        class="ocr-verification-products-table__header--align-right"
                    >
                        {{ $t.__('ocr.OcrVerificationProductsTable.amount_incl_tax') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell v-if="props.isLineItemsEnabled">
                        {{ $t.__('ocr.OcrVerificationProductsTable.catalogue_link_optional') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell>
                        {{ $t.__('common.labels.accounting_code') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell v-if="showAnalyticCodeColumn">
                        {{ $t.__('ocr.OcrVerificationProductsTable.analytic_code_optional') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell id="delete" fixed="right" :defaultWidth="80" />
                </CompassTableHead>
                <CompassTableBody>
                    <OcrVerificationProductsTableRow
                        v-for="(field, index) in productsArray.fields.value"
                        :key="field.key"
                        :disabled="props.disabled || props.isElectronic"
                        :index="index"
                        :canExpand="props.isStockEnabled && hasCatalogueLink(field.value)"
                        :isExpanded="expandedIndex === index"
                        :canDelete="canDelete"
                        :taxRates="props.taxRates"
                        :accountingCodes="props.accountingCodes"
                        :units="props.units"
                        :warehouses="props.warehouses"
                        :showAnalyticCode="props.showAnalyticCode"
                        :isLineItemsEnabled="props.isLineItemsEnabled"
                        :currencySymbol="props.currencySymbol"
                        :formatDateDisplayed="props.formatDateDisplayed"
                        :decimals="props.decimals"
                        @accounting-code-update="
                            (value, idx) => handleAccountingCodeUpdate(value, idx)
                        "
                        @catalogue-link-update="
                            (value, idx) => handleCatalogueLinkUpdate(value, idx)
                        "
                        @toggle-expand="(idx, expanded) => handleToggleExpand(idx, expanded)"
                        @request-delete="handleRequestProductDelete"
                        @request-duplicate="handleRequestDuplicate"
                        @product-updated="handleProductUpdated"
                    />
                </CompassTableBody>
            </CompassTable>
            <CompassButton
                class="ocr-verification-products-table__add-line"
                :label="$t.__('ocr.OcrVerificationForm.add_line')"
                :type="CompassEnums.ButtonType.TERTIARY"
                :disabled="props.disabled || props.isElectronic"
                leftIcon="plus"
                @click="handleAddProduct"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';
import { useFieldArray } from 'vee-validate';
import OcrVerificationProductsTableRow from 'vcomponents/ocr/OcrVerificationProductsTableRow.vue';
import Item from 'api/item';
import type { OcrProduct } from 'composables/ocr/ocrFormSchema';
import { setProductDescriptionFromAccountingCode } from 'composables/useOcrProducts';
import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { Unit } from 'types/entities/units/unit.type';
import type { Warehouse } from 'types/entities/stock/warehouse.type';
import type { PushToast } from 'types/injects/pushToast.type';
import type { Translator } from 'types/injects/translator.type';

interface Props {
    units?: Unit[];
    warehouses?: Warehouse[];
    isStockEnabled?: boolean;
    isLineItemsEnabled?: boolean;
    hint: string;
    accountingCodes: AccountingCode[];
    taxRates: TaxRate[];
    showAnalyticCode?: boolean;
    currencySymbol?: string;
    formatDateDisplayed?: string;
    addProduct: (product?: OcrProduct) => void;
    requestProductDelete: (index: number) => void;
    disabled?: boolean;
    isElectronic?: boolean;
    decimals: number;
}

interface ItemResponse {
    accounting_purchase_code_id: number | null;
    type: 'service' | 'item' | string;
}

const props = withDefaults(defineProps<Props>(), {
    units: () => [],
    warehouses: () => [],
    isStockEnabled: false,
    isLineItemsEnabled: false,
    showAnalyticCode: false,
    currencySymbol: undefined,
    formatDateDisplayed: undefined,
    disabled: false,
    isElectronic: false,
});

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const productsArray = useFieldArray<OcrProduct>('products');

const hasCatalogueLink = (product: OcrProduct): boolean => Boolean(product.catalogueLink);

const firstField = productsArray.fields.value[0];
const expandedIndex = ref<number | null>(
    firstField && hasCatalogueLink(firstField.value) ? 0 : null,
);

const canDelete = computed<boolean>(() => productsArray.fields.value.length > 1);
const showAnalyticCodeColumn = computed<boolean>(
    () => Boolean(props.showAnalyticCode) && Boolean(props.isLineItemsEnabled),
);

watch(
    () => productsArray.fields.value.length,
    (length, oldLength = 0) => {
        if (length > oldLength) {
            const firstField = productsArray.fields.value[0];
            if (oldLength === 0 && length > 0 && firstField && hasCatalogueLink(firstField.value)) {
                expandedIndex.value = 0;
            }
        } else {
            if (expandedIndex.value !== null && expandedIndex.value >= length) {
                expandedIndex.value = null;
            }
        }
    },
);

const handleAccountingCodeUpdate = (value: number | null, index: number): void => {
    const current = productsArray.fields.value[index]?.value;
    if (!current) {
        return;
    }
    const next: OcrProduct = { ...current, accountingCode: value };
    setProductDescriptionFromAccountingCode(next, props.accountingCodes);
    productsArray.update(index, next);
};

const handleCatalogueLinkUpdate = async (
    value: OcrProduct['catalogueLink'] | '',
    index: number,
): Promise<void> => {
    const current = productsArray.fields.value[index]?.value;
    if (!current) {
        return;
    }
    const next: OcrProduct = { ...current, catalogueLink: value === '' ? null : value };

    if (
        next.catalogueLink &&
        next.catalogueLink.id === undefined &&
        next.catalogueLink.value !== undefined
    ) {
        const numValue = Number(next.catalogueLink.value);
        next.catalogueLink.id = Number.isNaN(numValue) ? undefined : numValue;
    }

    const itemId = next.catalogueLink?.id;
    if (itemId) {
        try {
            const { data } = (await Item.getItemById(itemId)) as { data: ItemResponse };
            if (data.accounting_purchase_code_id) {
                next.accountingCode = data.accounting_purchase_code_id;
            }
            if (next.catalogueLink) {
                next.catalogueLink.itemType = data.type === 'service' ? 'service' : 'item';
            }
        } catch {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: $t.__('ocr.OcrVerificationForm.error_generic'),
                duration: 5000,
            });
        }
    }
    productsArray.update(index, next);
};

const handleToggleExpand = (index: number, expanded?: boolean): void => {
    const product = productsArray.fields.value[index]?.value;
    if (!product || !hasCatalogueLink(product)) {
        return;
    }
    const shouldExpand = typeof expanded === 'boolean' ? expanded : expandedIndex.value !== index;
    expandedIndex.value = shouldExpand ? index : null;
};

const handleProductUpdated = (
    itemId: number,
    updatedName: string,
    accountingPurchaseCodeId: number | null,
): void => {
    for (const [index, field] of productsArray.fields.value.entries()) {
        if (field.value.catalogueLink?.id === itemId) {
            const next: OcrProduct = {
                ...field.value,
                catalogueLink: { ...field.value.catalogueLink, label: updatedName },
            };
            if (accountingPurchaseCodeId) {
                next.accountingCode = accountingPurchaseCodeId;
            }
            productsArray.update(index, next);
        }
    }
};

const handleAddProduct = (): void => {
    props.addProduct();
};

const handleRequestProductDelete = (index: number): void => {
    props.requestProductDelete(index);
};

const handleRequestDuplicate = (index: number): void => {
    const current = productsArray.fields.value[index]?.value;
    if (current) {
        props.addProduct(current);
    }
};
</script>

<style lang="scss" scoped>
.ocr-verification-products-table__add-line {
    border: 0;
    padding-left: 0;
    margin-top: var(--spacing-2);
}

.ocr-verification-products-table__header--align-right {
    :deep(.compass-table__cell-tooltip) {
        justify-content: flex-end;
    }
}
</style>
